/**
 * @file contractProductGroups.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-29
 * @modified 2026-10-29
 * @description A contract made from a product group (Cam, 28 Oct 2026 — O1:
 *              product groups "can then inform contract pricing as well as
 *              pricing and costing"; his answer on what a contract for a group
 *              covers: "Plain product only, same pack"). Pure — the contract
 *              window, Contract these prices / the rep-reply import's panel
 *              and the server (services/contractProductGroupService,
 *              services/repReplyContractService) decide alike.
 *
 *   A group's members go on a contract as its product rows (the
 *   several-product model — one row per product sharing contract_group_id),
 *   so an order for ANY of them counts against it through the ordinary auto
 *   rule. Added: every member that is
 *     - the PLAIN product (no variant / additive label —
 *       inventory.items.product_group_additive is NULL),
 *     - not archived,
 *     - of the contract's category (its sales account — contractCategoryFor),
 *     - in the contract's PACK (a group is one per pack, but a hand-made one
 *       may hold several: the pack is the one of the contract's own product
 *       in the group, else the group's only pack, else the person chooses),
 *     - not on the contract already.
 *   Every other member is listed with why it was left out.
 *
 *   Client source; utils/contractProductGroups.js on the server is GENERATED
 *   from it by scripts/sync-shared-utils.js — edit this file, then run it.
 */

import { contractCategoryFor } from './pricingCosting';

/** The contract windows that hold several products (and so can take a group). */
export const GROUP_CONTRACT_CATEGORIES = [2, 3, 4, 7, 8];

/** The words for a contract category. */
export const CATEGORY_WORDS = { 2: 'blends', 3: 'compounds', 4: 'minerals', 7: 'bags', 8: 'other' };

/** Why an additive version is left out. */
export const PLAIN_ONLY = 'a contract on a group covers its plain product only';

/** Before the migration. */
export const NOT_READY_REASON = 'Not ready — migration contract_product_groups.sql has not run';

/** Before the product groups migration, or with none made yet. */
export const NO_GROUPS_TEXT = 'No product groups yet — they are made on Manage Inventory › Product Groups';

const idOf = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

/** The member's variant / additive label, or '' for the plain product. */
export const additiveOf = (m) => String((m && (m.additive ?? m.product_group_additive)) ?? '').trim();

/** Is a member the group's plain product (no variant / additive)? */
export const isPlainMember = (m) => !additiveOf(m);

/** Does a member belong in a contract of this category (its sales account)? */
export function fitsCategory(m, categoryId) {
  const c = contractCategoryFor(m && m.sales_account_code);
  return !c.refused && Number(c.id) === Number(categoryId);
}

/** "in bulk", "in totes" — a pack in words (the member's own words, else its key). */
const packWordsOf = (m) => (m && (m.pack_words || (m.pack ? `in ${m.pack}` : ''))) || 'its pack not known';

/**
 * The packs a group's plain, live members of this category come in, most
 * members first: [{ pack, words, count }].
 */
export function groupPacks(group, categoryId) {
  const by = new Map();
  for (const m of (group && group.members) || []) {
    if (m.is_archived || !isPlainMember(m) || !fitsCategory(m, categoryId)) continue;
    const key = m.pack || '';
    if (!by.has(key)) by.set(key, { pack: key, words: packWordsOf(m), count: 0 });
    by.get(key).count += 1;
  }
  return [...by.values()].sort((a, b) => (b.count - a.count) || a.words.localeCompare(b.words));
}

/**
 * What adding `group` to a contract would do. Pure.
 *
 * @param {object} group { id, name, is_archived, members: [{ id, item_code,
 *   item_name, is_archived, sales_account_code, additive, pack, pack_words,
 *   supplier?, cost? }] }
 * @param {object} opts
 * @param {number} opts.categoryId   the contract's category
 * @param {Array}  [opts.onContract] item ids already on the contract (any of its rows)
 * @param {string} [opts.pack]       the pack chosen (else worked out — see above)
 * @returns {{ refused: string|null, needsPack: boolean, pack: string|null,
 *   packWords: string|null, packs: object[], add: object[], already: object[],
 *   leftOut: Array<{ member, why }> }}
 */
export function groupAddPlan(group, { categoryId, onContract = [], pack = null } = {}) {
  const empty = { refused: null, needsPack: false, pack: null, packWords: null, packs: [], add: [], already: [], leftOut: [] };
  if (!group) return { ...empty, refused: 'Choose a product group' };
  if (group.is_archived) {
    return { ...empty, refused: `${group.name} is archived — restore it on Manage Inventory › Product Groups to use it` };
  }
  const words = CATEGORY_WORDS[categoryId] || 'contract';
  const members = group.members || [];
  const on = new Set((onContract || []).map(idOf).filter(n => n !== null));
  const packs = groupPacks(group, categoryId);

  // The pack: chosen, else the contract's own product in the group, else the only one.
  let chosen = pack !== null && pack !== undefined && pack !== '' ? String(pack) : null;
  if (chosen === null) {
    const own = members.find(m => on.has(idOf(m.id)) && isPlainMember(m) && fitsCategory(m, categoryId))
      || members.find(m => on.has(idOf(m.id)));
    if (own) chosen = own.pack || '';
    else if (packs.length === 1) chosen = packs[0].pack;
  }
  const needsPack = chosen === null && packs.length > 1;
  const chosenWords = chosen === null ? null
    : (packs.find(p => p.pack === chosen) || {}).words
      || packWordsOf(members.find(m => (m.pack || '') === chosen));

  const add = [];
  const already = [];
  const leftOut = [];
  for (const m of members) {
    if (on.has(idOf(m.id))) { already.push(m); continue; }
    let why = null;
    if (m.is_archived) why = 'Archived item';
    else if (!isPlainMember(m)) why = `Additive version (${additiveOf(m)}) — ${PLAIN_ONLY}`;
    else if (!fitsCategory(m, categoryId)) why = `Not a ${words} product (sales account ${m.sales_account_code || 'not set'})`;
    else if (chosen !== null && (m.pack || '') !== chosen) why = `Comes ${packWordsOf(m)} — this contract is ${chosenWords}`;
    if (why) leftOut.push({ member: m, why });
    else if (!needsPack) add.push(m);
  }
  let refused = null;
  if (!needsPack && !add.length) {
    refused = already.length
      ? `Every plain ${words} product of ${group.name} is on this contract already`
      : `${group.name} has no plain ${words} product to add`;
  }
  return { refused, needsPack, pack: chosen, packWords: chosenWords, packs, add, already, leftOut };
}

/**
 * The plain products the group has gained since the contract was made from
 * it (the same rule, the contract's own pack): the members to offer as "Add
 * <n> new products from the group". Pure.
 */
export function newMembersOf(group, { categoryId, onContract = [] } = {}) {
  if (!group || group.is_archived) return [];
  const plan = groupAddPlan(group, { categoryId, onContract });
  return plan.needsPack ? [] : plan.add;
}

/** "Group: Supreme Rearer 16" — the label a contract made from a group carries. */
export function groupLabel(g) {
  if (!g || !g.name) return '';
  return `Group: ${g.name}${g.is_archived ? ' (archived)' : ''}`;
}

/** "Add 2 new products from the group". */
export function addNewText(n) {
  return `Add ${n} new product${n === 1 ? '' : 's'} from the group`;
}

/** "SR16 Supreme Rearer 16 (NWF)" — a member in a line of text. */
export function memberText(m) {
  if (!m) return '';
  const supplier = m.supplier && (m.supplier.parent_name || m.supplier.name);
  return `${m.item_code || ''}${m.item_name ? ` ${m.item_name}` : ''}${supplier ? ` (${supplier})` : ''}`.trim();
}

/**
 * "3 plain products will be added: A, B, C." — the plan in a sentence;
 * `names: false` stops at the colon (the window lists them underneath).
 */
export function planSentence(plan, group, { names = true } = {}) {
  if (!plan) return '';
  if (plan.refused) return plan.refused;
  if (plan.needsPack) return `${group.name} comes in ${plan.packs.length} packs — choose which this contract is for (a contract on a group covers one pack).`;
  const n = plan.add.length;
  // "Supreme Rearer 16 in totes" already says its pack.
  const pack = plan.packWords && !String(group.name || '').toLowerCase().includes(plan.packWords.toLowerCase()) ? ` ${plan.packWords}` : '';
  const head = `${n} plain product${n === 1 ? '' : 's'} of ${group.name}${pack} will be added`;
  return names ? `${head}: ${plan.add.map(memberText).join('; ')}.` : `${head}:`;
}

/**
 * A group's members to add alongside a priced row's product (Contract these
 * prices / the rep-reply import — "Add the rest of <group>'s plain products
 * to this contract"): the same rule, the row's product's pack, the row's
 * product (and the other rows of the contract) counted as on it already.
 */
export function restOfGroupFor(group, { categoryId, itemId, onContract = [] } = {}) {
  if (!group || group.is_archived) return null;
  const own = (group.members || []).find(m => idOf(m.id) === idOf(itemId));
  if (!own) return null;
  const plan = groupAddPlan(group, { categoryId, onContract: [itemId, ...(onContract || [])], pack: own.pack || '' });
  return plan;
}
